/**
 * Squishy cards.
 *
 * Howard proposed adding "squishies"; Kate defined every rule, and then defined them a
 * second time unprompted after he warned "the AI may not know what a squishy is":
 *
 *   "So a squishy card is like... actually, they're animal cards. If you collect an
 *    animal card you can sell them to other players. And the rarer the animal is in real
 *    life, uh, the more, um, money you can get from other players... I'll sell this for
 *    200 coupons. And if that other player accepts, you get their 200 coupons, and they
 *    get your card. So you can sell rare cards for more money, and the more you sell,
 *    when you get a card, it upgrades it to another level."
 *
 *   "And all the— so there's less rare, like the endangered species in real life, there's
 *    less of in the card games. And, um, they're all random, so whenever you get a card,
 *    it's random. But it's only when you win a competition."
 *
 * So: animal cards, competition-win drops only, random, tradeable by offer-and-accept,
 * scarcity tracking real-world endangerment, duplicates upgrading a card's level.
 *
 * Because she tied scarcity to real life, the tiers below use each animal's actual IUCN
 * conservation status rather than invented rarities.
 */
import type { SpeciesId } from '../animals/species'

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'

/** Kate's rule, made concrete: rarer in life → fewer in the pool → worth more. */
const RARITY: Record<Rarity, { weight: number; baseValue: number; label: string; color: string }> = {
  common:    { weight: 100, baseValue: 20,  label: 'Least Concern',         color: '#7fae5e' },
  uncommon:  { weight: 45,  baseValue: 60,  label: 'Near Threatened',       color: '#c9c05a' },
  rare:      { weight: 18,  baseValue: 140, label: 'Vulnerable',            color: '#e0913c' },
  epic:      { weight: 7,   baseValue: 320, label: 'Endangered',            color: '#e0553c' },
  legendary: { weight: 2,   baseValue: 700, label: 'Critically Endangered', color: '#c04ce0' },
}

export interface CardKind {
  species: SpeciesId
  /** The real animal on the card, so the rarity means something. */
  realName: string
  rarity: Rarity
  /** A true fact, because the scarcity is drawn from the real world. */
  fact: string
}

/**
 * One card per species, using a real member of that family and its actual conservation
 * status. The Bactrian camel really is Critically Endangered; the European rabbit really
 * is Endangered, which is the sort of thing that makes the mechanic worth having.
 */
export const CARD_KINDS: CardKind[] = [
  { species: 'wolf', realName: 'Gray Wolf', rarity: 'common',
    fact: 'A pack can roam over 1,000 square miles.' },
  { species: 'deer', realName: 'Red Deer', rarity: 'common',
    fact: 'Stags regrow their antlers every single year.' },
  { species: 'cat', realName: 'Domestic Cat', rarity: 'common',
    fact: 'Can rotate its ears 180 degrees to pinpoint a sound.' },
  { species: 'dog', realName: 'Domestic Dog', rarity: 'common',
    fact: 'Smells roughly 100,000 times better than a human.' },
  { species: 'chameleon', realName: 'Panther Chameleon', rarity: 'uncommon',
    fact: 'Changes colour to signal mood, not just to hide.' },
  { species: 'bird', realName: 'Hyacinth Macaw', rarity: 'rare',
    fact: 'The largest flying parrot in the world.' },
  { species: 'bear', realName: 'Polar Bear', rarity: 'rare',
    fact: 'Its fur is not white — it is hollow and clear.' },
  { species: 'fish', realName: 'Atlantic Cod', rarity: 'rare',
    fact: 'A single female can carry five million eggs.' },
  { species: 'bunny', realName: 'European Rabbit', rarity: 'epic',
    fact: 'Endangered in its native Spain, despite being common elsewhere.' },
  { species: 'dolphin', realName: 'Amazon River Dolphin', rarity: 'epic',
    fact: 'Lives in fresh water and is faintly pink.' },
  { species: 'whale', realName: 'Blue Whale', rarity: 'epic',
    fact: 'The largest animal that has ever lived.' },
  { species: 'camel', realName: 'Bactrian Camel', rarity: 'legendary',
    fact: 'Fewer than 1,000 remain wild in the Gobi Desert.' },
]

/** Kate: "the more you sell, when you get a card, it upgrades it to another level." */
export const MAX_LEVEL = 5

export interface OwnedCard {
  species: SpeciesId
  /** 1..MAX_LEVEL. Duplicates push this up. */
  level: number
  /** How many of this card have been collected in total. */
  copies: number
}

export interface TradeOffer {
  id: number
  /** Who is offering the card. */
  fromName: string
  species: SpeciesId
  level: number
  /** Coupons being asked for. */
  askingPrice: number
}

export function kindOf(species: SpeciesId): CardKind | undefined {
  return CARD_KINDS.find((c) => c.species === species)
}

export function rarityInfo(rarity: Rarity) {
  return RARITY[rarity]
}

/** What a card is worth: its rarity's base value, raised by its level. */
export function cardValue(species: SpeciesId, level: number): number {
  const kind = kindOf(species)
  if (!kind) return 0
  return Math.round(RARITY[kind.rarity].baseValue * (1 + (level - 1) * 0.6))
}

export class CardCollection {
  private readonly owned = new Map<SpeciesId, OwnedCard>()
  private readonly random: () => number
  private nextOfferId = 1
  /** Offers other players have made, awaiting accept or decline. */
  readonly incomingOffers: TradeOffer[] = []

  constructor(seed = 8080) {
    let a = seed >>> 0
    this.random = () => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
  }

  get all(): OwnedCard[] {
    // Rarest first, so a good pull is visible immediately.
    const order: Rarity[] = ['legendary', 'epic', 'rare', 'uncommon', 'common']
    return [...this.owned.values()].sort((a, b) => {
      const ra = kindOf(a.species)!.rarity
      const rb = kindOf(b.species)!.rarity
      return order.indexOf(ra) - order.indexOf(rb)
    })
  }

  get distinctCount(): number {
    return this.owned.size
  }

  get totalCount(): number {
    return [...this.owned.values()].reduce((sum, c) => sum + c.copies, 0)
  }

  has(species: SpeciesId): boolean {
    return this.owned.has(species)
  }

  get(species: SpeciesId): OwnedCard | undefined {
    return this.owned.get(species)
  }

  /**
   * Draw a random card, weighted so endangered animals are genuinely scarce.
   * Kate: "they're all random, so whenever you get a card, it's random."
   */
  drawRandomKind(): CardKind {
    const total = CARD_KINDS.reduce((sum, k) => sum + RARITY[k.rarity].weight, 0)
    let ticket = this.random() * total
    for (const kind of CARD_KINDS) {
      ticket -= RARITY[kind.rarity].weight
      if (ticket <= 0) return kind
    }
    return CARD_KINDS[CARD_KINDS.length - 1]!
  }

  /**
   * Award a card. This is the ONLY way cards enter the collection, and it is called
   * only on a competition win — Kate: "it's only when you win a competition."
   *
   * Returns the card and whether a duplicate upgraded its level.
   */
  award(kind: CardKind = this.drawRandomKind()): { kind: CardKind; card: OwnedCard; upgraded: boolean } {
    const existing = this.owned.get(kind.species)
    if (!existing) {
      const card: OwnedCard = { species: kind.species, level: 1, copies: 1 }
      this.owned.set(kind.species, card)
      return { kind, card, upgraded: false }
    }
    existing.copies++
    const upgraded = existing.level < MAX_LEVEL
    if (upgraded) existing.level++
    return { kind, card: existing, upgraded }
  }

  /** Remove a card, e.g. after selling it to another player. */
  give(species: SpeciesId): OwnedCard | null {
    const card = this.owned.get(species)
    if (!card) return null
    this.owned.delete(species)
    return card
  }

  /** Take a card in, e.g. after buying one. Duplicates upgrade as normal. */
  receive(species: SpeciesId, level: number): void {
    const existing = this.owned.get(species)
    if (!existing) {
      this.owned.set(species, { species, level, copies: 1 })
      return
    }
    existing.copies++
    if (existing.level < MAX_LEVEL) existing.level++
  }

  /** Another player offers to buy one of your cards for coupons. */
  makeIncomingOffer(fromName: string, species: SpeciesId): TradeOffer | null {
    const card = this.owned.get(species)
    if (!card) return null
    // They offer near market value, give or take.
    const value = cardValue(species, card.level)
    const offer: TradeOffer = {
      id: this.nextOfferId++,
      fromName,
      species,
      level: card.level,
      askingPrice: Math.round(value * (0.8 + this.random() * 0.5)),
    }
    this.incomingOffers.push(offer)
    return offer
  }

  /**
   * Accept an offer: Kate's exact flow — "if that other player accepts, you get their
   * 200 coupons, and they get your card."
   */
  acceptOffer(id: number): { coupons: number; species: SpeciesId } | null {
    const index = this.incomingOffers.findIndex((o) => o.id === id)
    if (index < 0) return null
    const offer = this.incomingOffers[index]!
    if (!this.owned.has(offer.species)) return null
    this.incomingOffers.splice(index, 1)
    this.give(offer.species)
    return { coupons: offer.askingPrice, species: offer.species }
  }

  declineOffer(id: number): void {
    const index = this.incomingOffers.findIndex((o) => o.id === id)
    if (index >= 0) this.incomingOffers.splice(index, 1)
  }
}
